import { useCallback, useMemo } from 'react';
import _get from 'lodash/get';
import { ErrorLike, WatchQueryFetchPolicy } from '@apollo/client';
import {
  FilterGroupingEnum,
  Task,
  TaskFilterInputObject,
  TaskScopeInputObject,
  TaskScopeNameEnum,
  TasksMultiselectListQueryVariables,
  useTasksMultiselectIdsLazyQuery,
  useTasksMultiselectListQuery,
} from '@lib/graphql-common';
import {
  GetUrlParams,
  OrderingParams,
  PaginationParams,
} from '@lib/enums/urls';
import createSearchFilters, {
  FilterSchemaPredicateType,
} from '@lib/utils/createSearchFilters';
import { FetchIdsResponse } from '@lib/utils/getAllTableItemsIds';

type Props = {
  additionalFilters?: object[];
  fetchPolicy?: WatchQueryFetchPolicy;
  // TaskFilterInputObject has no `id` filter; filter by group_key instead so
  // the Selected tab can fetch exactly the chosen recurring-task chains.
  groupKeys?: string[];
  orderingParams?: OrderingParams;
  paginationParams?: PaginationParams;
  scope?: TaskScopeInputObject;
  searchQuery?: string;
  skip?: boolean;
  usePrevious?: boolean;
};

export type UseTasksMultiselectQueryHookResult = {
  collection: Task[];
  error: ErrorLike | undefined;
  fetchIds: () => Promise<FetchIdsResponse<unknown>>;
  firstLoading: boolean;
  loading: boolean;
  refetch: () => Promise<unknown>;
  totalCount: number;
  totalPages: number;
};

const SEARCH_SCHEMA = [
  { fieldName: 'name' },
  { fieldName: 'formCategoryName' },
  { fieldName: 'siteName' },
  { fieldName: 'siteAreaName' },
  { fieldName: 'siteCode' },
  { fieldName: 'siteAreaCode' },
  { fieldName: 'assetName' },
  { fieldName: 'assetCode' },
  { fieldName: 'priority', predicateType: FilterSchemaPredicateType.Eq },
];

export default function useTasksMultiselectQueryHook({
  additionalFilters,
  fetchPolicy = 'cache-and-network',
  groupKeys,
  orderingParams,
  paginationParams,
  scope,
  searchQuery,
  skip,
  usePrevious = true,
}: Props): UseTasksMultiselectQueryHookResult {
  const page = _get(paginationParams, GetUrlParams.Page);
  const perPage = _get(paginationParams, GetUrlParams.PerPage);
  const orderingField = _get(orderingParams, GetUrlParams.OrderingField);
  const orderingDirection = _get(
    orderingParams,
    GetUrlParams.OrderingDirection,
  );

  const resolvedScope: TaskScopeInputObject = useMemo(
    () => scope ?? { name: TaskScopeNameEnum.Active },
    [scope],
  );

  const pageNumber = Number(page);
  const limitNumber = Number(perPage);
  const variables: TasksMultiselectListQueryVariables = {
    scope: resolvedScope,
    page: Number.isNaN(pageNumber) ? undefined : pageNumber,
    limit: Number.isNaN(limitNumber) ? undefined : limitNumber,
  };

  const filters: TaskFilterInputObject[] = [];

  if (searchQuery) {
    filters.push(...createSearchFilters(searchQuery, SEARCH_SCHEMA));
  }

  if (groupKeys?.length) {
    filters.push({
      groupKey: {
        grouping: FilterGroupingEnum.And,
        predicate: { in: groupKeys },
        filterGroup: 'groupKey',
      },
    });
  }

  if (additionalFilters?.length) {
    filters.push(...(additionalFilters as TaskFilterInputObject[]));
  }

  if (filters.length) {
    variables.filters = filters;
  }

  if (orderingField && orderingDirection) {
    variables.sorts = {
      [orderingField]: orderingDirection,
    };
  }

  const { data, previousData, loading, error, refetch } =
    useTasksMultiselectListQuery({
      fetchPolicy,
      variables,
      skip,
    });

  const [fetchIdsQuery] = useTasksMultiselectIdsLazyQuery({
    fetchPolicy: 'no-cache',
  });
  const fetchIds = useCallback(
    () =>
      fetchIdsQuery({
        variables: {
          filters: variables.filters,
          scope: resolvedScope,
        },
      }),
    [fetchIdsQuery, variables.filters, resolvedScope],
  );

  const queryData = usePrevious ? data || previousData : data;
  const firstLoading = loading && previousData === undefined;
  const collection =
    !usePrevious && loading
      ? []
      : (_get(queryData, 'tasks.collection', []) as Task[]);
  const metadata = _get(queryData, 'tasks.metadata');
  const totalCount = _get(metadata, 'totalCount', 0);
  const totalPages = _get(metadata, 'totalPages', 0);

  return {
    collection,
    error,
    fetchIds: fetchIds as () => Promise<FetchIdsResponse<unknown>>,
    firstLoading,
    loading,
    refetch,
    totalCount,
    totalPages,
  };
}
